/**
 * Drawer de detalle de una plantilla de turno (008 · T044 · US1; contracts/ui.md §1.3).
 *
 * Muestra la versión vigente y la tabla de **todas** las versiones con su vigencia (FR-002: las
 * anteriores nunca se modifican). Acciones con `schedules.configure`: "Cambiar horario" (nueva
 * versión), "Editar nombre" y "Archivar" (con confirmación; archivar no cambia asignaciones,
 * FR-003). Una plantilla de integración o archivada no ofrece acciones; con la empresa suspendida
 * quedan deshabilitadas (solo lectura).
 */
import { Archive, Clock, Lock, Pencil } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import type { ShiftTemplateDetail } from '@fermin/contracts';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import { Drawer, DrawerContent } from '../../../components/ui/drawer';
import { SkeletonLines } from '../../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../../../components/ui/table';
import { useToast } from '../../../components/ui/toast';
import { formatDateTime, t } from '../../../i18n';
import { managedLabel, useIntegrationSystemName } from '../../integration/managed-by';
import { readOnlyProps, useReadOnly } from '../../shell/use-read-only';
import { scheduleErrorMessage, useArchiveShiftTemplate, useShiftTemplate } from '../api';
import { durationLabel, formatLocalDay, timeRange, toleranceLabel } from '../format';

import { TemplateFormDialog } from './template-form-dialog';
import { TemplateVersionDialog } from './template-version-dialog';

export interface TemplateDrawerProps {
  templateId: string | null;
  onClose: () => void;
  canConfigure: boolean;
  companyTimezone: string;
  companyLateTolerance: number | null;
  companyEarlyTolerance: number | null;
}

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-fm-1">
      <dt className="text-fm-xs font-medium text-fm-text-secondary">{label}</dt>
      <dd className="text-fm-sm text-fm-text">{children}</dd>
    </div>
  );
}

export function TemplateDrawer({
  templateId,
  onClose,
  canConfigure,
  companyTimezone,
  companyLateTolerance,
  companyEarlyTolerance,
}: TemplateDrawerProps) {
  const query = useShiftTemplate(templateId);
  const template = query.data;
  const { readOnly } = useReadOnly();
  const { toast } = useToast();
  const archive = useArchiveShiftTemplate();
  // 009 · US6 (T055): nombre del sistema para "Gestionado por <sistema>".
  const systemName = useIntegrationSystemName();
  const [versionOpen, setVersionOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const editable =
    canConfigure &&
    template !== undefined &&
    template.status === 'ACTIVE' &&
    template.source === 'MANUAL';

  function confirmArchive(detail: ShiftTemplateDetail) {
    archive.mutate(
      { id: detail.id },
      {
        onSuccess: () => {
          toast({
            title: t('schedules.templates.toast.archived', { name: detail.name }),
            variant: 'success',
          });
          setArchiveOpen(false);
        },
        onError: (error) => {
          toast({ title: scheduleErrorMessage(error), variant: 'error' });
        },
      },
    );
  }

  return (
    <Drawer open={templateId !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <DrawerContent
        title={
          template ? `${template.code} · ${template.name}` : t('schedules.templates.drawer.title')
        }
        description={t('schedules.templates.drawer.description')}
        actions={
          editable ? (
            <>
              <Button
                variant="compact"
                size="sm"
                icon={<Clock size={16} />}
                data-testid="shift-template-change-times"
                onClick={() => setVersionOpen(true)}
                {...readOnlyProps(readOnly)}
              >
                {t('schedules.templates.drawer.changeTimes')}
              </Button>
            </>
          ) : null
        }
        footer={
          editable ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                icon={<Pencil size={16} />}
                data-testid="shift-template-rename"
                onClick={() => setRenameOpen(true)}
                {...readOnlyProps(readOnly)}
              >
                {t('schedules.templates.drawer.rename')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<Archive size={16} />}
                data-testid="shift-template-archive"
                onClick={() => setArchiveOpen(true)}
                {...readOnlyProps(readOnly)}
              >
                {t('schedules.templates.drawer.archive')}
              </Button>
            </>
          ) : undefined
        }
      >
        {query.isPending && templateId !== null ? <SkeletonLines lines={5} /> : null}
        {query.isError ? (
          <p role="alert" className="text-fm-sm text-fm-danger-text">
            {scheduleErrorMessage(query.error)}
          </p>
        ) : null}

        {template ? (
          <div className="flex flex-col gap-fm-5" data-testid="shift-template-detail">
            {template.source === 'INTEGRATION' ? (
              <p
                data-testid="shift-template-managed"
                className="flex items-start gap-fm-2 rounded-fm-md bg-fm-bg-sunken p-fm-3 text-fm-sm text-fm-text-secondary"
              >
                <Lock size={16} aria-hidden="true" className="mt-px shrink-0" />
                <span>
                  <span className="font-semibold text-fm-text">{managedLabel(systemName)}</span>
                  {'. '}
                  {t('schedules.managed.templateNotice')}
                </span>
              </p>
            ) : null}
            {template.status === 'ARCHIVED' ? (
              <p className="rounded-fm-md bg-fm-bg-sunken p-fm-3 text-fm-sm text-fm-text-secondary">
                {template.archived_at
                  ? t('schedules.templates.drawer.archivedAt', {
                      when: formatDateTime(template.archived_at, companyTimezone),
                      who: template.archived_by?.name ?? t('schedules.history.system'),
                    })
                  : t('schedules.templates.status.ARCHIVED')}
              </p>
            ) : null}

            <dl className="grid grid-cols-2 gap-fm-4">
              <SummaryRow label={t('schedules.templates.columns.schedule')}>
                <span className="fm-tabular">
                  {timeRange(template.current.start_time, template.current.end_time)}
                </span>
                {template.crosses_midnight ? (
                  <span className="block text-fm-xs text-fm-text-secondary">
                    {t('schedules.templates.crossesMidnight')}
                  </span>
                ) : null}
              </SummaryRow>
              <SummaryRow label={t('schedules.templates.columns.duration')}>
                {durationLabel(template.duration_minutes)}
              </SummaryRow>
              <SummaryRow label={t('schedules.templates.columns.break')}>
                {t('schedules.format.minutes', { minutes: template.current.break_minutes })}
              </SummaryRow>
              <SummaryRow label={t('schedules.templates.columns.tolerances')}>
                {toleranceLabel(template.current, companyLateTolerance, companyEarlyTolerance)}
              </SummaryRow>
              <SummaryRow label={t('schedules.templates.columns.source')}>
                {t(`schedules.source.${template.source}`)}
              </SummaryRow>
              <SummaryRow label={t('schedules.templates.columns.status')}>
                {template.status === 'ACTIVE' ? (
                  <Badge variant="activa">{t('schedules.templates.status.ACTIVE')}</Badge>
                ) : (
                  <Badge variant="inactiva">{t('schedules.templates.status.ARCHIVED')}</Badge>
                )}
                <span className="mt-fm-1 block text-fm-xs text-fm-text-secondary">
                  {template.in_use
                    ? t('schedules.templates.inUse')
                    : t('schedules.templates.notInUse')}
                </span>
              </SummaryRow>
            </dl>

            <section className="flex flex-col gap-fm-2">
              <h3 className="font-display text-fm-lg font-semibold text-fm-text">
                {t('schedules.templates.drawer.versions')}
              </h3>
              <Table data-testid="shift-template-versions">
                <TableCaption>{t('schedules.templates.drawer.versions')}</TableCaption>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>
                      {t('schedules.templates.versionColumns.version')}
                    </TableHeaderCell>
                    <TableHeaderCell>
                      {t('schedules.templates.versionColumns.from')}
                    </TableHeaderCell>
                    <TableHeaderCell>
                      {t('schedules.templates.versionColumns.schedule')}
                    </TableHeaderCell>
                    <TableHeaderCell>
                      {t('schedules.templates.versionColumns.detail')}
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {template.versions.map((version) => (
                    <TableRow key={version.id}>
                      <TableCell>
                        {t('schedules.templates.versionLabel', { version: version.version })}
                        {version.id === template.current.id ? (
                          <span className="ml-fm-1 text-fm-xs text-fm-success-text">
                            {t('schedules.templates.current')}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="fm-tabular">
                        {version.effective_from
                          ? formatLocalDay(version.effective_from)
                          : t('schedules.templates.fromStart')}
                      </TableCell>
                      <TableCell className="fm-tabular">
                        {timeRange(version.start_time, version.end_time)}
                      </TableCell>
                      <TableCell>
                        <span className="flex flex-col py-fm-1 text-fm-xs text-fm-text-secondary">
                          <span>
                            {t('schedules.templates.versionBreak', {
                              minutes: version.break_minutes,
                            })}{' '}
                            · {toleranceLabel(version, companyLateTolerance, companyEarlyTolerance)}
                          </span>
                          <span>
                            {t('schedules.templates.versionCreated', {
                              when: formatDateTime(version.created_at, companyTimezone),
                              who: version.created_by?.name ?? t('schedules.history.system'),
                            })}
                          </span>
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </section>
          </div>
        ) : null}
        {template && editable ? (
          <>
            <TemplateVersionDialog
              open={versionOpen}
              onOpenChange={setVersionOpen}
              template={template}
              companyTimezone={companyTimezone}
              companyLateTolerance={companyLateTolerance}
              companyEarlyTolerance={companyEarlyTolerance}
            />
            <TemplateFormDialog
              open={renameOpen}
              onOpenChange={setRenameOpen}
              template={template}
              companyLateTolerance={companyLateTolerance}
              companyEarlyTolerance={companyEarlyTolerance}
            />
            <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
              <DialogContent
                title={t('schedules.templates.archive.title', { name: template.name })}
                description={t('schedules.templates.archive.description')}
                footer={
                  <>
                    <Button
                      variant="ghost"
                      onClick={() => setArchiveOpen(false)}
                      disabled={archive.isPending}
                    >
                      {t('common.buttons.cancel')}
                    </Button>
                    <Button
                      variant="destructive"
                      data-testid="shift-template-archive-confirm"
                      loading={archive.isPending}
                      loadingText={t('common.states.processing')}
                      onClick={() => confirmArchive(template)}
                      {...readOnlyProps(readOnly)}
                    >
                      {t('schedules.templates.archive.confirm')}
                    </Button>
                  </>
                }
              >
                {template.in_use ? <p>{t('schedules.templates.archive.inUse')}</p> : null}
              </DialogContent>
            </Dialog>
          </>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}
